import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { bindAll } from '@bibliocommons/utils-react';
import { bibShape, shelfItemShape, ugcContentAdvisoryShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import FullScreenOverlay from '@bibliocommons/deprecated-base-full-screen-overlay';
import { ExclamationMarkTriangle } from '@bibliocommons/deprecated-base-icons';
import { ExpandableList } from '@bibliocommons/deprecated-expandable';
import { allowFreeText } from '@bibliocommons/utils-user';

import DisplayActions from '../../../shared/UGC/DisplayActions';
import ContentAdvisoryDisplay from '../../../shared/UGC/ContentAdvisoryDisplay';
import ContentAdvisoryUGCEditor from '../../../shared/UGC/UGCEditors/ContentAdvisoryUGCEditor';

import './ContentAdvisoryInfo.scss';

function renderTriggerContent(expanded) {
  return expanded ? <FormattedMessage id="button_hide_details" /> : <FormattedMessage id="button_show_details" />;
}

export class ShelfListItemContentAdvisoryInfo extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);

    this.state = {
      showEditor: false
    };
  }

  openEditor() {
    this.setState({ showEditor: true });
  }

  closeEditor() {
    this.setState({ showEditor: false });
  }

  renderActions() {
    if (this.props.owner.get('loggedIn')) {
      const { contentAdvisoriesActions, ownerContentAdvisoriesForBib, contentAdvisoriesTransactions, bib } = this.props;
      const contentAdvisoryIds = ownerContentAdvisoriesForBib.map(contentAdvisory => contentAdvisory.get('id')).toJS();
      const handleRemove = () =>
        contentAdvisoriesActions.removeContentAdvisories({
          contentAdvisoryIds
        });
      const isRemoving = ownerContentAdvisoriesForBib.some(contentAdvisory =>
        contentAdvisoriesTransactions.getIn([contentAdvisory.get('id'), 'isRemoving'], false)
      );
      const title = bib.getIn(['briefInfo', 'title']);
      const editScreenReaderMessage = <FormattedMessage id="ada_ugc_edit_content_advisories" values={{ title }} />;
      const removeScreenReaderMessage = <FormattedMessage id="ada_ugc_remove_content_advisories" values={{ title }} />;
      return (
        <DisplayActions
          handleEdit={allowFreeText(this.props.owner) ? this.openEditor : undefined}
          handleRemove={handleRemove}
          isRemoving={isRemoving}
          editScreenReaderMessage={editScreenReaderMessage}
          removeScreenReaderMessage={removeScreenReaderMessage}
        />
      );
    }
    return null;
  }

  renderEditor() {
    const {
      bib,
      item,
      shelfActions,
      shelfTransactions,
      ownerContentAdvisoriesForBib,
      contentAdvisoriesActions,
      contentAdvisoriesTransactions
    } = this.props;

    return (
      this.state.showEditor && (
        <ContentAdvisoryUGCEditor
          bib={bib}
          existingAdvisories={ownerContentAdvisoriesForBib}
          shelfItem={item}
          handleClose={this.closeEditor}
          contentAdvisoriesActions={contentAdvisoriesActions}
          contentAdvisoriesTransactions={contentAdvisoriesTransactions}
          shelfTransactions={shelfTransactions}
          shelfActions={shelfActions}
        />
      )
    );
  }

  renderContentAdvisoryTypes() {
    const { intl, ownerContentAdvisoriesForBib } = this.props;

    return ownerContentAdvisoriesForBib
      .map(notice => intl.formatMessage({ id: `${notice.get('type')}_advisory_type` }))
      .join(', ');
  }

  renderContentAdvisoryHeader() {
    return (
      <div className="content-advisory-header">
        <ExclamationMarkTriangle />
        <FormattedMessage id="content_advisory_types" values={{ types: this.renderContentAdvisoryTypes() }} />
        {this.renderActions()}
      </div>
    );
  }

  renderContentAdvisoryDisplay(notice) {
    const { owner } = this.props;

    return <ContentAdvisoryDisplay owner={owner} notice={notice} />;
  }

  render() {
    const { ownerContentAdvisoriesForBib } = this.props;

    if (ownerContentAdvisoriesForBib && ownerContentAdvisoriesForBib.size > 0) {
      return (
        <div className="cp-shelf-list-item-content-advisories-info">
          {this.renderContentAdvisoryHeader()}
          <ExpandableList
            collapseCount={0}
            itemClassName="content-advisory"
            list={ownerContentAdvisoriesForBib}
            getItemKey={notice => notice.get('id')}
            renderItem={this.renderContentAdvisoryDisplay}
            renderExpandButtonLabel={expanded => renderTriggerContent(expanded)}
          />
          <FullScreenOverlay open={!!this.state.showEditor} handleClose={this.closeEditor}>
            {this.renderEditor()}
          </FullScreenOverlay>
        </div>
      );
    }

    return null;
  }
}

ShelfListItemContentAdvisoryInfo.propTypes = {
  bib: bibShape.isRequired,
  item: shelfItemShape.isRequired,
  owner: userIdentityShape.isRequired,
  ownerContentAdvisoriesForBib: ImmutablePropTypes.listOf(ugcContentAdvisoryShape),
  contentAdvisoriesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  contentAdvisoriesTransactions: ImmutablePropTypes.map.isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired
};

export default injectIntl(ShelfListItemContentAdvisoryInfo);
